import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  Users,
  Send,
  MessageSquare,
  CheckCircle2,
  ArrowRight,
  Zap,
  Command,
  ShieldCheck,
  Globe,
  Database,
  Search,
  MousePointer,
  Sparkles,
  CheckCheck,
  Star,
  Link2,
} from "lucide-react";

export const AIAgentAppleKeynote: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Apple Architectural Dark Palette
  const bgMain = "#000000";
  const titaniumWhite = "#F5F5F7";
  const titaniumMuted = "#9CA3AF";
  const titaniumBorder = "rgba(255, 255, 255, 0.16)";
  const titaniumGlass = "rgba(255, 255, 255, 0.04)";
  const appleGreen = "#30D158";
  const appleBlue = "#2997FF";

  // Calibrated Apple Spring Helper (damping 15.35, stiffness 115, mass 1.0)
  const sp = (delay: number, damping = 15.35, stiffness = 115) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1.0 },
    });

  // Subtitle synchronization (1350 frames total = 45.0s @ 30fps)
  const SUBTITLES = [
    { from: 0, to: 110, text: "Belajar AI sendirian hari ini adalah cara paling cepat untuk tertinggal.", highlight: "paling cepat untuk tertinggal" },
    { from: 120, to: 200, text: "Dunia sudah bergerak melewati chatbot biasa.", highlight: "melewati chatbot biasa" },
    { from: 210, to: 340, text: "Sekarang eranya AI Agent, teknologi yang bisa berpikir dan bekerja mandiri.", highlight: "AI Agent" },
    { from: 350, to: 475, text: "Untuk itu, kami membangun ruang belajar bersama di Telegram dan WhatsApp.", highlight: "Telegram dan WhatsApp" },
    { from: 485, to: 650, text: "Tempat kita membedah perkembangan AI Agent, menguji tools terbaru, dan berdiskusi secara nyata.", highlight: "berdiskusi secara nyata" },
    { from: 660, to: 720, text: "Bukan untuk jualan kelas.", highlight: "Bukan untuk jualan kelas" },
    { from: 725, to: 820, text: "Tujuannya murni membangun circle yang saling mendukung.", highlight: "membangun circle" },
    { from: 825, to: 970, text: "Dari yang baru mulai hingga yang sudah berpengalaman, semua saling berbagi wawasan setiap hari.", highlight: "berbagi wawasan" },
    { from: 980, to: 1135, text: "Di era teknologi yang bergerak begitu cepat, memiliki komunitas yang tepat adalah akselerasi terbaik.", highlight: "akselerasi terbaik" },
    { from: 1145, to: 1230, text: "Akses komunitas ini masih terbuka gratis.", highlight: "terbuka gratis" },
    { from: 1235, to: 1350, text: "Klik link di bio, dan join buat belajar bareng sekarang.", highlight: "join buat belajar bareng" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  const subScale = currentSub
    ? spring({
        frame: frame - currentSub.from,
        fps,
        config: { damping: 14, stiffness: 180 },
      })
    : 1;

  // Scene Visibility Windows
  const showScene1 = frame >= 0 && frame < 115;
  const showScene2 = frame >= 115 && frame < 345;
  const showScene3 = frame >= 345 && frame < 655;
  const showScene4 = frame >= 655 && frame < 975;
  const showScene5 = frame >= 975 && frame < 1140;
  const showScene6 = frame >= 1140 && frame <= 1350;

  // Camera drift
  const cameraScale = interpolate(frame, [0, 1350], [1.0, 1.04]);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgMain,
        color: titaniumWhite,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Liberation Sans", sans-serif',
        overflow: "hidden",
      }}
    >
      {/* Primary Voiceover Track */}
      <Audio
        src={staticFile("ai_agent_assets/community_voice_45s.mp3")}
        volume={1.0}
      />

      {/* Ambient BGM */}
      <Audio
        src={staticFile("bgm-clean.mp3")}
        volume={0.07}
        loop
      />

      {/* Frame-Accurate Micro-SFX */}
      {frame === 0 && <Audio src={staticFile("ui-pop.mp3")} volume={0.6} />}
      {frame === 25 && <Audio src={staticFile("ui-pop.mp3")} volume={0.8} />}
      {frame === 115 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.5} />}
      {frame === 345 && <Audio src={staticFile("chime.mp3")} volume={0.65} />}
      {frame === 370 && <Audio src={staticFile("pop.mp3")} volume={0.4} />}
      {frame === 430 && <Audio src={staticFile("pop.mp3")} volume={0.4} />}
      {frame === 500 && <Audio src={staticFile("pop.mp3")} volume={0.5} />}
      {frame === 655 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.5} />}
      {frame === 975 && <Audio src={staticFile("click-soft.mp3")} volume={0.7} />}
      {frame === 1140 && <Audio src={staticFile("ui-success.mp3")} volume={0.8} />}

      {/* Radial Background Spotlight */}
      <div
        style={{
          position: "absolute",
          top: "15%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 900,
          height: 900,
          background:
            "radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, rgba(0, 0, 0, 0) 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Top Header Pill (Safe Zone: Y: 180 - 250px) */}
      <div
        style={{
          position: "absolute",
          top: 180,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 40,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 24px",
            borderRadius: 999,
            background: "rgba(255, 255, 255, 0.05)",
            border: `1px solid ${titaniumBorder}`,
            backdropFilter: "blur(12px)",
          }}
        >
          <Command size={18} color={titaniumWhite} />
          <span
            style={{
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: titaniumWhite,
              textTransform: "uppercase",
            }}
          >
            AI Agent Community
          </span>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: appleGreen,
              boxShadow: `0 0 10px ${appleGreen}`,
            }}
          />
        </div>
      </div>

      {/* Main Staging Container */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 1080,
          height: 1920,
          transform: `scale(${cameraScale})`,
          transformOrigin: "center center",
        }}
      >
        {/* ======================================================== */}
        {/* SCENE 1: THE HOOK (Frames 0 - 115)                       */}
        {/* Visual: Smartphone mock lebar, chat ChatGPT kontras &    */}
        {/* stempel merah 'TERTINGGAL 2 TAHUN' di bawah teks         */}
        {/* ======================================================== */}
        {showScene1 && (() => {
          const t1Spring = sp(0, 16, 130);
          const phoneSpring = sp(8, 15, 120);
          const stampSpring = sp(20, 12, 160);

          return (
            <div
              style={{
                position: "absolute",
                top: 340, // Centered optically
                left: 70,
                right: 70,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* Headline */}
              <div
                style={{
                  textAlign: "center",
                  fontSize: 56,
                  fontWeight: 900,
                  letterSpacing: "-0.04em",
                  color: titaniumWhite,
                  lineHeight: 1.15,
                  transform: `translateY(${interpolate(t1Spring, [0, 1], [30, 0])}px)`,
                  opacity: interpolate(t1Spring, [0, 1], [0, 1]),
                }}
              >
                Belajar AI Sendirian?
              </div>

              {/* Realistic Smartphone Frame (Spacious & Perfectly Balanced) */}
              <div
                style={{
                  marginTop: 36,
                  width: 780,
                  borderRadius: 44,
                  background: "#0F1216",
                  border: "2.5px solid rgba(255, 255, 255, 0.22)",
                  boxShadow: "0 40px 100px rgba(0,0,0,0.95)",
                  padding: "32px 36px 44px",
                  position: "relative",
                  transform: `scale(${interpolate(phoneSpring, [0, 1], [0.88, 1])})`,
                  opacity: interpolate(phoneSpring, [0, 1], [0, 1]),
                  display: "flex",
                  flexDirection: "column",
                  gap: 24,
                }}
              >
                {/* Dynamic Island / Speaker Notch */}
                <div
                  style={{
                    width: 140,
                    height: 8,
                    borderRadius: 999,
                    background: "rgba(255, 255, 255, 0.25)",
                    margin: "0 auto 12px",
                  }}
                />

                {/* User Question Bubble (100% Unblocked) */}
                <div
                  style={{
                    alignSelf: "flex-end",
                    background: "#22252A",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    color: "#FFFFFF",
                    padding: "20px 26px",
                    borderRadius: "24px 24px 4px 24px",
                    fontSize: 24,
                    fontWeight: 700,
                    maxWidth: "88%",
                  }}
                >
                  Tolong riset harga kompetitor & bikinin laporannya dong...
                </div>

                {/* Chatbot Limited Answer Bubble (100% Unblocked, High Contrast) */}
                <div
                  style={{
                    alignSelf: "flex-start",
                    background: "rgba(255, 255, 255, 0.08)",
                    border: "1px solid rgba(255, 255, 255, 0.22)",
                    color: "#F3F4F6",
                    padding: "22px 26px",
                    borderRadius: "24px 24px 24px 4px",
                    fontSize: 22,
                    lineHeight: 1.45,
                    fontWeight: 600,
                    maxWidth: "92%",
                  }}
                >
                  Maaf, saya cuma chatbot teks. Saya tidak bisa buka internet, tidak bisa download file, dan kamu tetap harus kerja manual.
                </div>

                {/* Big Red Warning Stamp (Positioned below the text so zero letters are covered) */}
                <div
                  style={{
                    alignSelf: "center",
                    marginTop: 12,
                    transform: `rotate(-4deg) scale(${interpolate(stampSpring, [0, 1], [1.8, 1])})`,
                    opacity: interpolate(stampSpring, [0, 1], [0, 1]),
                    padding: "18px 48px",
                    borderRadius: 20,
                    background: "rgba(220, 38, 38, 0.96)",
                    boxShadow: "0 20px 60px rgba(220, 38, 38, 0.7)",
                    border: "3px solid #FFFFFF",
                    color: "#FFFFFF",
                    fontSize: 34,
                    fontWeight: 900,
                    letterSpacing: "0.08em",
                    whiteSpace: "nowrap",
                    textTransform: "uppercase",
                  }}
                >
                  TERTINGGAL 2 TAHUN!
                </div>
              </div>
            </div>
          );
        })()}

        {/* ======================================================== */}
        {/* SCENE 2: THE PARADIGM SHIFT (Frames 115 - 345)           */}
        {/* Visual: Live interactive workflow simulasi apa itu agent  */}
        {/* ======================================================== */}
        {showScene2 && (() => {
          const shiftSpring = sp(120, 16, 120);
          const step1 = sp(150, 15, 130);
          const step2 = sp(210, 15, 130);
          const step3 = sp(270, 15, 130);

          return (
            <div
              style={{
                position: "absolute",
                top: 270,
                left: 80,
                right: 80,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* Header Title */}
              <div
                style={{
                  textAlign: "center",
                  fontSize: 50,
                  fontWeight: 900,
                  letterSpacing: "-0.03em",
                  color: titaniumWhite,
                  lineHeight: 1.15,
                  transform: `translateY(${interpolate(shiftSpring, [0, 1], [30, 0])}px)`,
                  opacity: interpolate(shiftSpring, [0, 1], [0, 1]),
                }}
              >
                Bukan Chatbot Biasa.
                <br />
                <span style={{ color: appleGreen }}>Ini AI Yang Bekerja Mandiri.</span>
              </div>

              {/* Interactive Simulation Console */}
              <div
                style={{
                  marginTop: 36,
                  width: "100%",
                  borderRadius: 36,
                  background:
                    "linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)",
                  border: "2px solid rgba(255, 255, 255, 0.22)",
                  boxShadow: "0 35px 90px rgba(0,0,0,0.9)",
                  padding: "36px 32px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 22,
                  transform: `scale(${interpolate(shiftSpring, [0, 1], [0.92, 1])})`,
                }}
              >
                {/* Single Prompt Box */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    padding: "18px 24px",
                    borderRadius: 20,
                    background: "rgba(0,0,0,0.65)",
                    border: "1px solid rgba(255, 255, 255, 0.16)",
                  }}
                >
                  <Search size={24} color={appleBlue} />
                  <span style={{ fontSize: 22, fontWeight: 800, color: titaniumWhite }}>
                    "Riset tren AI minggu ini & rekap ke WhatsApp"
                  </span>
                </div>

                {/* Step 1 */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 18,
                    padding: "20px 24px",
                    borderRadius: 22,
                    background: titaniumGlass,
                    border: `1.5px solid ${titaniumBorder}`,
                    transform: `translateX(${interpolate(step1, [0, 1], [-30, 0])}px)`,
                    opacity: interpolate(step1, [0, 1], [0, 1]),
                  }}
                >
                  <Globe size={30} color={appleBlue} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 24, fontWeight: 900, color: titaniumWhite }}>
                      Buka Web & Riset Mandiri
                    </div>
                    <div style={{ fontSize: 17, color: titaniumMuted, marginTop: 2 }}>
                      Membaca 12 website berita & mengekstrak data
                    </div>
                  </div>
                  <CheckCircle2 size={26} color={appleGreen} />
                </div>

                {/* Step 2 */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 18,
                    padding: "20px 24px",
                    borderRadius: 22,
                    background: titaniumGlass,
                    border: `1.5px solid ${titaniumBorder}`,
                    transform: `translateX(${interpolate(step2, [0, 1], [-30, 0])}px)`,
                    opacity: interpolate(step2, [0, 1], [0, 1]),
                  }}
                >
                  <Database size={30} color="#A855F7" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 24, fontWeight: 900, color: titaniumWhite }}>
                      Olah & Sortir Informasi
                    </div>
                    <div style={{ fontSize: 17, color: titaniumMuted, marginTop: 2 }}>
                      Menyaring poin penting tanpa halusinasi
                    </div>
                  </div>
                  <CheckCircle2 size={26} color={appleGreen} />
                </div>

                {/* Step 3 */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 18,
                    padding: "20px 24px",
                    borderRadius: 22,
                    background: "rgba(48, 209, 88, 0.14)",
                    border: `2px solid ${appleGreen}`,
                    transform: `translateX(${interpolate(step3, [0, 1], [-30, 0])}px)`,
                    opacity: interpolate(step3, [0, 1], [0, 1]),
                  }}
                >
                  <MessageSquare size={30} color={appleGreen} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 24, fontWeight: 900, color: appleGreen }}>
                      Kirim Laporan Lengkap
                    </div>
                    <div style={{ fontSize: 17, color: titaniumWhite, marginTop: 2 }}>
                      Selesai otomatis langsung ke WhatsApp kamu
                    </div>
                  </div>
                  <CheckCircle2 size={26} color={appleGreen} />
                </div>
              </div>
            </div>
          );
        })()}

        {/* ======================================================== */}
        {/* SCENE 3: REAL CHAT COMMUNITY MOCKUP (Frames 345 - 655)   */}
        {/* Visual: Simulated live WhatsApp/Telegram group chats!    */}
        {/* ======================================================== */}
        {showScene3 && (() => {
          const chatBoxSpring = sp(350, 15, 120);
          const msg1 = sp(370, 14, 130);
          const msg2 = sp(430, 14, 130);
          const msg3 = sp(500, 14, 130);

          return (
            <div
              style={{
                position: "absolute",
                top: 270,
                left: 80,
                right: 80,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* Header */}
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  letterSpacing: "0.12em",
                  color: appleGreen,
                  textTransform: "uppercase",
                  textAlign: "center",
                  marginBottom: 10,
                }}
              >
                100% Edukasi Terbuka
              </div>

              <div
                style={{
                  fontSize: 50,
                  fontWeight: 900,
                  letterSpacing: "-0.03em",
                  color: titaniumWhite,
                  textAlign: "center",
                  lineHeight: 1.15,
                }}
              >
                Grup Telegram & WhatsApp
              </div>

              {/* Realistic Chat App Mockup Window */}
              <div
                style={{
                  marginTop: 32,
                  width: "100%",
                  borderRadius: 36,
                  background: "#0E1117",
                  border: "2px solid rgba(255, 255, 255, 0.2)",
                  boxShadow: "0 40px 100px rgba(0,0,0,0.95)",
                  overflow: "hidden",
                  transform: `scale(${interpolate(chatBoxSpring, [0, 1], [0.92, 1])})`,
                }}
              >
                {/* Chat App Header Bar */}
                <div
                  style={{
                    padding: "22px 28px",
                    background: "#161B22",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                  }}
                >
                  <div
                    style={{
                      width: 54,
                      height: 54,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #2563EB, #10B981)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#FFFFFF",
                      fontWeight: 900,
                      fontSize: 24,
                    }}
                  >
                    AI
                  </div>
                  <div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: titaniumWhite }}>
                      AI Agent Indonesia
                    </div>
                    <div style={{ fontSize: 16, color: appleGreen, fontWeight: 700 }}>
                      1,480 Anggota Aktif  |  Diskusi Terbuka
                    </div>
                  </div>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 12 }}>
                    <Send size={26} color={appleBlue} />
                    <MessageSquare size={26} color={appleGreen} />
                  </div>
                </div>

                {/* Simulated Real Community Chats */}
                <div style={{ padding: "28px 24px", display: "flex", flexDirection: "column", gap: 20 }}>
                  {/* Chat 1 */}
                  <div
                    style={{
                      display: "flex",
                      gap: 14,
                      opacity: interpolate(msg1, [0, 1], [0, 1]),
                      transform: `translateY(${interpolate(msg1, [0, 1], [25, 0])}px)`,
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        background: "#3B82F6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        fontWeight: 900,
                        color: "#FFFFFF",
                      }}
                    >
                      D
                    </div>
                    <div
                      style={{
                        background: "rgba(255, 255, 255, 0.08)",
                        padding: "16px 22px",
                        borderRadius: "0 22px 22px 22px",
                        maxWidth: "82%",
                        position: "relative",
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#60A5FA" }}>Dimas (Praktisi)</div>
                      <div style={{ fontSize: 18, color: titaniumWhite, marginTop: 4, lineHeight: 1.4 }}>
                        Agent buat bikin bot auto-reply WhatsApp kemarin gila daging banget! Hemat waktu seharian.
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 4, marginTop: 6 }}>
                        <span style={{ fontSize: 13, color: titaniumMuted }}>09:41</span>
                        <CheckCheck size={14} color={appleBlue} />
                      </div>
                    </div>
                  </div>

                  {/* Chat 2 */}
                  <div
                    style={{
                      display: "flex",
                      gap: 14,
                      opacity: interpolate(msg2, [0, 1], [0, 1]),
                      transform: `translateY(${interpolate(msg2, [0, 1], [25, 0])}px)`,
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        background: "#EC4899",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        fontWeight: 900,
                        color: "#FFFFFF",
                      }}
                    >
                      R
                    </div>
                    <div
                      style={{
                        background: "rgba(255, 255, 255, 0.08)",
                        padding: "16px 22px",
                        borderRadius: "0 22px 22px 22px",
                        maxWidth: "82%",
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#F472B6" }}>Rina (Pemula)</div>
                      <div style={{ fontSize: 18, color: titaniumWhite, marginTop: 4, lineHeight: 1.4 }}>
                        Mau nanya dong, pemula tanpa ngoding cocoknya mulai dari tool yang mana dulu ya?
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 4, marginTop: 6 }}>
                        <span style={{ fontSize: 13, color: titaniumMuted }}>09:42</span>
                        <CheckCheck size={14} color={appleBlue} />
                      </div>
                    </div>
                  </div>

                  {/* Chat 3 (Admin Solution) */}
                  <div
                    style={{
                      display: "flex",
                      gap: 14,
                      opacity: interpolate(msg3, [0, 1], [0, 1]),
                      transform: `translateY(${interpolate(msg3, [0, 1], [25, 0])}px)`,
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        background: appleGreen,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Star size={22} color="#000000" fill="#000000" />
                    </div>
                    <div
                      style={{
                        background: "rgba(48, 209, 88, 0.16)",
                        border: "1.5px solid rgba(48, 209, 88, 0.4)",
                        padding: "16px 22px",
                        borderRadius: "0 22px 22px 22px",
                        maxWidth: "84%",
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 900, color: appleGreen }}>Community Admin</div>
                      <div style={{ fontSize: 18, color: titaniumWhite, marginTop: 4, lineHeight: 1.4 }}>
                        Panduan langkah demi langkah pemula udah ada di pinned message grup ya, gratis tinggal comot!
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 4, marginTop: 6 }}>
                        <span style={{ fontSize: 13, color: appleGreen }}>09:43</span>
                        <CheckCheck size={14} color={appleGreen} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ======================================================== */}
        {/* SCENE 4: CIRCLE VALUE (Frames 655 - 975)                 */}
        {/* Visual: Penegasan 'Bukan Jualan Kelas' + 3 Benefit Nyata */}
        {/* ======================================================== */}
        {showScene4 && (() => {
          const card1 = sp(660, 15, 125);
          const card2 = sp(720, 15, 125);
          const card3 = sp(780, 15, 125);
          const card4 = sp(840, 15, 125);

          return (
            <div
              style={{
                position: "absolute",
                top: 270,
                left: 80,
                right: 80,
                display: "flex",
                flexDirection: "column",
                gap: 22,
              }}
            >
              {/* Highlight Banner: Bukan Jualan Kelas */}
              <div
                style={{
                  padding: "26px 32px",
                  borderRadius: 26,
                  background:
                    "linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(0, 0, 0, 0.5) 100%)",
                  border: "2px solid rgba(239, 68, 68, 0.45)",
                  display: "flex",
                  alignItems: "center",
                  gap: 20,
                  transform: `translateY(${interpolate(card1, [0, 1], [30, 0])}px)`,
                  opacity: interpolate(card1, [0, 1], [0, 1]),
                }}
              >
                <ShieldCheck size={42} color="#EF4444" />
                <div>
                  <div style={{ fontSize: 30, fontWeight: 900, color: "#FFFFFF" }}>
                    Bukan Untuk Jualan Kelas
                  </div>
                  <div style={{ fontSize: 19, color: titaniumMuted, marginTop: 4 }}>
                    Nol biaya tersembunyi. Tempat murni belajar & bertumbuh.
                  </div>
                </div>
              </div>

              {/* Benefit 1 */}
              <div
                style={{
                  padding: "24px 28px",
                  borderRadius: 22,
                  background: titaniumGlass,
                  border: `1.5px solid ${titaniumBorder}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 18,
                  transform: `translateY(${interpolate(card2, [0, 1], [30, 0])}px)`,
                  opacity: interpolate(card2, [0, 1], [0, 1]),
                }}
              >
                <Sparkles size={34} color={appleBlue} />
                <div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: titaniumWhite }}>
                    Kupas Tuntas Tools AI Terbaru
                  </div>
                  <div style={{ fontSize: 17, color: titaniumMuted, marginTop: 2 }}>
                    Tiap minggu kita bedah mana yang beneran berguna
                  </div>
                </div>
              </div>

              {/* Benefit 2 */}
              <div
                style={{
                  padding: "24px 28px",
                  borderRadius: 22,
                  background: titaniumGlass,
                  border: `1.5px solid ${titaniumBorder}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 18,
                  transform: `translateY(${interpolate(card3, [0, 1], [30, 0])}px)`,
                  opacity: interpolate(card3, [0, 1], [0, 1]),
                }}
              >
                <Zap size={34} color="#FBBF24" />
                <div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: titaniumWhite }}>
                    Template Otomasi Siap Pakai
                  </div>
                  <div style={{ fontSize: 17, color: titaniumMuted, marginTop: 2 }}>
                    Tinggal copy-paste alur kerja yang sudah terbukti jalan
                  </div>
                </div>
              </div>

              {/* Benefit 3 */}
              <div
                style={{
                  padding: "24px 28px",
                  borderRadius: 22,
                  background: titaniumGlass,
                  border: `1.5px solid ${titaniumBorder}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 18,
                  transform: `translateY(${interpolate(card4, [0, 1], [30, 0])}px)`,
                  opacity: interpolate(card4, [0, 1], [0, 1]),
                }}
              >
                <Users size={34} color={appleGreen} />
                <div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: titaniumWhite }}>
                    Circle Saling Membantu
                  </div>
                  <div style={{ fontSize: 17, color: titaniumMuted, marginTop: 2 }}>
                    Bebas tanya jawab tanpa takut dihakimi pemula
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ======================================================== */}
        {/* SCENE 5: THE LEVERAGE & STATS (Frames 975 - 1140)        */}
        {/* Visual: Network constellation + 3 Stat Highlights       */}
        {/* ======================================================== */}
        {showScene5 && (() => {
          const coreSpring = sp(980, 16, 120);

          return (
            <div
              style={{
                position: "absolute",
                top: 300,
                left: 80,
                right: 80,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  letterSpacing: "0.12em",
                  color: appleGreen,
                  textTransform: "uppercase",
                  marginBottom: 16,
                }}
              >
                Kunci Akselerasi
              </div>

              <div
                style={{
                  fontSize: 54,
                  fontWeight: 900,
                  lineHeight: 1.15,
                  letterSpacing: "-0.04em",
                  color: titaniumWhite,
                  transform: `scale(${interpolate(coreSpring, [0, 1], [0.92, 1])})`,
                }}
              >
                Punya Circle yang Tepat
                <br />
                Adalah Jalan Pintas Terbaik.
              </div>

              {/* Stat Metric Trio */}
              <div
                style={{
                  marginTop: 48,
                  display: "flex",
                  justifyContent: "space-around",
                  width: "100%",
                  padding: "36px 20px",
                  borderRadius: 32,
                  background: "rgba(255, 255, 255, 0.04)",
                  border: `2px solid ${titaniumBorder}`,
                  boxShadow: "0 30px 80px rgba(0,0,0,0.85)",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 48, fontWeight: 900, color: titaniumWhite, letterSpacing: "-0.03em" }}>
                    1,400+
                  </div>
                  <div style={{ fontSize: 18, color: titaniumMuted, marginTop: 6 }}>Anggota Aktif</div>
                </div>
                <div style={{ width: 1, height: 74, background: titaniumBorder }} />
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 48, fontWeight: 900, color: appleGreen, letterSpacing: "-0.03em" }}>
                    10x
                  </div>
                  <div style={{ fontSize: 18, color: titaniumMuted, marginTop: 6 }}>Lebih Cepat Paham</div>
                </div>
                <div style={{ width: 1, height: 74, background: titaniumBorder }} />
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 48, fontWeight: 900, color: appleBlue, letterSpacing: "-0.03em" }}>
                    Rp 0
                  </div>
                  <div style={{ fontSize: 18, color: titaniumMuted, marginTop: 6 }}>Biaya Gabung</div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ======================================================== */}
        {/* SCENE 6: LINK IN BIO CTA SIMULATION (Frames 1140 - 1350) */}
        {/* Visual: Simulated social profile link clicking           */}
        {/* ======================================================== */}
        {showScene6 && (() => {
          const finalSpring = sp(1145, 15, 120);
          const bioSpring = sp(1170, 14, 130);
          const cursorSpring = sp(1200, 12, 140);
          const cursorClick = frame >= 1235 ? 0.85 : 1.0;

          return (
            <div
              style={{
                position: "absolute",
                top: 270,
                left: 80,
                right: 80,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  transform: `scale(${interpolate(finalSpring, [0, 1], [0.9, 1])})`,
                  opacity: interpolate(finalSpring, [0, 1], [0, 1]),
                }}
              >
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: "0.12em",
                    color: appleGreen,
                    textTransform: "uppercase",
                  }}
                >
                  Pintu Masuk Terbuka Gratis
                </div>
                <div
                  style={{
                    marginTop: 14,
                    fontSize: 52,
                    fontWeight: 900,
                    lineHeight: 1.15,
                    letterSpacing: "-0.04em",
                    color: titaniumWhite,
                  }}
                >
                  Belajar & Bertumbuh
                  <br />
                  Bersama Kami.
                </div>
              </div>

              {/* Realistic Social Profile Bio Card */}
              <div
                style={{
                  marginTop: 36,
                  width: "100%",
                  borderRadius: 32,
                  background: "#111418",
                  border: "2px solid rgba(255, 255, 255, 0.22)",
                  boxShadow: "0 35px 90px rgba(0,0,0,0.9)",
                  padding: "28px 28px",
                  position: "relative",
                  transform: `translateY(${interpolate(bioSpring, [0, 1], [30, 0])}px)`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                  <div
                    style={{
                      width: 58,
                      height: 58,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #10B981, #2563EB)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#FFFFFF",
                      fontSize: 26,
                      fontWeight: 900,
                    }}
                  >
                    AI
                  </div>
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: 24, fontWeight: 900, color: titaniumWhite }}>
                      Komunitas AI Agent
                    </div>
                    <div style={{ fontSize: 16, color: titaniumMuted }}>
                      @ai.agent.community
                    </div>
                  </div>
                </div>

                {/* Simulated Link in Bio Pill */}
                <div
                  style={{
                    marginTop: 22,
                    padding: "18px 24px",
                    borderRadius: 18,
                    background: "rgba(255, 255, 255, 0.08)",
                    border: "1.5px solid rgba(255, 255, 255, 0.28)",
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                  }}
                >
                  <Link2 size={24} color={appleBlue} />
                  <span style={{ fontSize: 20, fontWeight: 800, color: appleBlue }}>
                    linktr.ee/aiagent-community
                  </span>
                </div>

                {/* Animated Mouse Pointer / Cursor */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 26,
                    right: 56,
                    transform: `translateY(${interpolate(cursorSpring, [0, 1], [30, 0])}px) scale(${cursorClick})`,
                    opacity: interpolate(cursorSpring, [0, 1], [0, 1]),
                  }}
                >
                  <MousePointer size={38} color="#FFFFFF" fill="#FFFFFF" />
                </div>
              </div>

              {/* Apple White Solid CTA Button */}
              <div
                style={{
                  marginTop: 36,
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  padding: "24px 56px",
                  borderRadius: 999,
                  background: titaniumWhite,
                  boxShadow: "0 25px 80px rgba(255, 255, 255, 0.35)",
                }}
              >
                <span
                  style={{
                    fontSize: 28,
                    fontWeight: 900,
                    letterSpacing: "-0.02em",
                    color: "#000000",
                  }}
                >
                  Join Belajar Bareng Sekarang
                </span>
                <ArrowRight size={28} color="#000000" />
              </div>

              <div
                style={{
                  marginTop: 24,
                  fontSize: 18,
                  color: titaniumMuted,
                  letterSpacing: "0.02em",
                }}
              >
                Tersedia di Telegram & WhatsApp  |  Klik Link di Bio
              </div>
            </div>
          );
        })()}
      </div>

      {/* ======================================================== */}
      {/* GOLDEN SAFE SUBTITLE BAND (Y: 1490px / bottom: 430px)    */}
      {/* ======================================================== */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 430,
            left: 0,
            width: 1080,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 100,
            pointerEvents: "none",
            boxSizing: "border-box",
            padding: "0 90px",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(10, 10, 10, 0.94)",
              backdropFilter: "blur(20px)",
              padding: "18px 36px",
              borderRadius: 26,
              border: "1px solid rgba(255, 255, 255, 0.22)",
              boxShadow: "0 25px 60px rgba(0,0,0,0.9)",
              maxWidth: 840,
              width: "auto",
              textAlign: "center",
              transform: `scale(${subScale})`,
              transformOrigin: "center center",
              boxSizing: "border-box",
              lineHeight: 1.45,
            }}
          >
            <div
              style={{
                fontSize: 28,
                fontWeight: 800,
                color: "#FFFFFF",
                letterSpacing: "-0.01em",
                display: "inline",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              {currentSub.text.split(" ").map((word, idx) => {
                const isHighlight =
                  currentSub.highlight &&
                  currentSub.highlight.toLowerCase().includes(word.toLowerCase());
                return (
                  <span
                    key={idx}
                    style={{
                      color: isHighlight ? appleGreen : "#FFFFFF",
                      marginRight: 8,
                      display: "inline-block",
                    }}
                  >
                    {word}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
